// ════════════════════════════════════════════════════════════
// RegistrarFacturaProveedor — registrar la factura que envía el proveedor
// Se usa desde Administración (Facturas a pagar) y desde el pedido en Compras
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Select,
  MenuItem, FormControl, InputLabel, Alert, Autocomplete, Typography, Box
} from '@mui/material';
import {
  getConfigERP, getProveedoresCompras, registrarFacturaProveedor, crearFacturaProveedor,
  subirArchivoFacturaProveedor
} from '../api';

const REGIMENES = [
  { clave: 'nacional',         nombre: 'Nacional (con IVA)' },
  { clave: 'intracomunitaria', nombre: 'Unión Europea (sin IVA, autorrepercusión)' },
  { clave: 'importacion',      nombre: 'Importación (IVA en aduana, DUA)' },
];
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const r2 = (n: number) => Math.round(n * 100) / 100;

export default function RegistrarFacturaProveedor({ open, factura, onClose, onGuardada }: any) {
  const [formas, setFormas] = useState<any[]>([]);
  const [proveedores, setProveedores] = useState<any[]>([]);
  const [proveedor, setProveedor] = useState<any>(null);
  const [d, setD] = useState<any>({});
  const [ivaPct, setIvaPct] = useState(21);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [guardando, setGuardando] = useState(false);
  const nueva = !factura;

  useEffect(() => {
    if (!open) return;
    getConfigERP().then((c: any) => setFormas(c.formas_pago || [])).catch(() => {});
    if (nueva) getProveedoresCompras().then(setProveedores).catch(() => {});
    const regimen = factura?.regimen_compra || 'nacional';
    const base = factura?.base_imponible || 0;
    const cuota = factura?.cuota_iva || 0;
    setIvaPct(regimen !== 'nacional' ? 0 : (base > 0 ? Math.round(cuota / base * 100) : 21));
    setD({
      numero_proveedor: factura?.numero_proveedor || '',
      fecha: (factura?.estado === 'registrada' && factura?.fecha) || new Date().toISOString().slice(0, 10),
      regimen_compra: regimen,
      base_imponible: base || '',
      cuota_iva: cuota || '',
      forma_pago: factura?.forma_pago || 'contado',
      dua_numero: factura?.dua_numero || '',
      observaciones: factura?.observaciones || '',
    });
    setProveedor(null);
    setArchivo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, factura]);

  const cambiarBase = (valor: string) => {
    const base = parseFloat(valor) || 0;
    setD({ ...d, base_imponible: valor, cuota_iva: r2(base * ivaPct / 100) });
  };
  const cambiarIva = (pct: number) => {
    setIvaPct(pct);
    setD({ ...d, cuota_iva: r2((parseFloat(d.base_imponible) || 0) * pct / 100) });
  };
  const cambiarRegimen = (regimen: string) => {
    const pct = regimen === 'nacional' ? 21 : 0;
    setIvaPct(pct);
    setD({ ...d, regimen_compra: regimen, cuota_iva: r2((parseFloat(d.base_imponible) || 0) * pct / 100) });
  };

  const total = r2((parseFloat(d.base_imponible) || 0) + (parseFloat(d.cuota_iva) || 0));
  const totalPedido = factura?.pedido_id ? (factura.total || 0) : null;
  const diferencia = totalPedido !== null && factura?.estado === 'pendiente_recibir'
    ? r2(total - totalPedido) : 0;
  const esImportacion = d.regimen_compra === 'importacion';

  const guardar = async () => {
    if (nueva && !proveedor) { alert('Selecciona el proveedor'); return; }
    if (!d.numero_proveedor?.trim()) { alert('Indica el número de la factura del proveedor'); return; }
    setGuardando(true);
    try {
      const datos = {
        ...d, numero_proveedor: d.numero_proveedor.trim(),
        base_imponible: parseFloat(d.base_imponible) || 0,
        cuota_iva: parseFloat(d.cuota_iva) || 0, total,
      };
      let id = factura?.id;
      if (nueva) {
        const r = await crearFacturaProveedor({ ...datos, proveedor_id: proveedor.id, linea_negocio: 'otros' });
        id = r.id;
      } else {
        await registrarFacturaProveedor(factura.id, datos);
      }
      if (archivo && id) await subirArchivoFacturaProveedor(id, archivo);
      if (onGuardada) onGuardada();
      onClose();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al registrar la factura');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth
      onClose={(e, rz) => { if (rz !== 'backdropClick' && rz !== 'escapeKeyDown') onClose(); }}>
      <DialogTitle>
        🧾 {nueva ? 'Factura de proveedor sin pedido' : `Factura de ${factura?.proveedor_nombre}`}
      </DialogTitle>
      <DialogContent>
        {!nueva && factura?.observaciones && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {factura.observaciones}{totalPedido !== null ? ` · Importe del pedido: ${eur(totalPedido)}` : ''}
          </Typography>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {nueva && (
            <Grid item xs={12}>
              <Autocomplete options={proveedores} value={proveedor}
                getOptionLabel={(p: any) => p.nombre || ''}
                onChange={(_, v: any) => {
                  setProveedor(v);
                  if (v && v.regimen_compra) cambiarRegimen(v.regimen_compra);
                }}
                renderInput={(p) => <TextField {...p} size="small" label="Proveedor *" />} />
            </Grid>
          )}
          <Grid item xs={7}>
            <TextField fullWidth size="small" label="Nº de factura del proveedor *"
              value={d.numero_proveedor || ''}
              onChange={(e) => setD({ ...d, numero_proveedor: e.target.value })} />
          </Grid>
          <Grid item xs={5}>
            <TextField fullWidth size="small" type="date" label="Fecha factura"
              InputLabelProps={{ shrink: true }} value={d.fecha || ''}
              onChange={(e) => setD({ ...d, fecha: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Régimen fiscal</InputLabel>
              <Select value={d.regimen_compra || 'nacional'} label="Régimen fiscal"
                onChange={(e) => cambiarRegimen(String(e.target.value))}>
                {REGIMENES.map(r => <MenuItem key={r.clave} value={r.clave}>{r.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={5}>
            <TextField fullWidth size="small" type="number" label="Base imponible"
              value={d.base_imponible} onChange={(e) => cambiarBase(e.target.value)} />
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel>IVA</InputLabel>
              <Select value={ivaPct} label="IVA" onChange={(e) => cambiarIva(Number(e.target.value))}>
                {[21, 10, 4, 0].map(p => <MenuItem key={p} value={p}>{p}%</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Cuota IVA"
              value={d.cuota_iva} onChange={(e) => setD({ ...d, cuota_iva: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography sx={{ fontWeight: 700 }}>Total factura</Typography>
              <Typography sx={{ fontWeight: 800 }}>{eur(total)}</Typography>
            </Box>
            {diferencia !== 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                El total difiere del pedido en {eur(diferencia)}. Revisa si incluye portes u otros cargos.
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={esImportacion ? 6 : 12}>
            <FormControl fullWidth size="small">
              <InputLabel>Forma de pago</InputLabel>
              <Select value={formas.some((f: any) => f.clave === d.forma_pago) ? d.forma_pago : 'contado'}
                label="Forma de pago" onChange={(e) => setD({ ...d, forma_pago: e.target.value })}>
                {formas.map((f: any) => <MenuItem key={f.clave} value={f.clave}>{f.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {esImportacion && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Nº de DUA" value={d.dua_numero || ''}
                onChange={(e) => setD({ ...d, dua_numero: e.target.value })} />
            </Grid>
          )}
          <Grid item xs={12}>
            <Button variant="outlined" component="label" fullWidth>
              {archivo
                ? `📎 ${archivo.name}`
                : (factura?.archivo ? '📎 Sustituir el documento adjunto' : '📎 Adjuntar factura (PDF o foto)')}
              <input type="file" hidden accept=".pdf,image/*"
                onChange={(e) => setArchivo(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
            </Button>
          </Grid>
        </Grid>
</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={guardando} sx={{ bgcolor: '#4f46e5' }}>
          {guardando ? 'Guardando...' : 'Registrar factura'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
